
import React, {
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { useTranslation } from "react-i18next";
import { connect } from "react-redux";

import {
  ClearRounded,
  VisibilityRounded,
} from "@mui/icons-material";

import {
  Box,
  Button,
  Divider,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";

import { Alert, Dialog, Table } from "../../layout";

import * as actions from "../../api/actions/pos";
import { endpoints } from "../../api/actions/api";
import { DropLi } from "../../hooks";
import { AppContext } from "../../App";
import { formatCurrency } from "../../hooks/Currency";

import ReportPrintButton from "../reports/ReportPrintButton";

//------------------------------------------------
// Reusable Salary Payment Details
//------------------------------------------------
const SalaryDetailsContent = ({ salary, store, t }) => {
  const displayMoney = (value) => {
    if (
      value === null ||
      value === undefined ||
      value === ""
    ) {
      return "-";
    }

    return formatCurrency(Number(value), store);
  };

  const fields = [
    {
      label: t("firstName"),
      value: salary?.firstName,
    },
    {
      label: t("lastName"),
      value: salary?.lastName,
    },
    {
      label: t("dateOfBirth"),
      value: salary?.dob,
    },
    {
      label: t("salaryType"),
      value: salary?.salaryType,
    },
    {
      label: t("salary"),
      value: displayMoney(salary?.salary),
    },
    {
      label: t("payPeriod"),
      value: salary?.payDate,
    },
    {
      label: t("totalWorkHour"),
      value: salary?.totalHours,
    },
    {
      label: t("totalSalary"),
      value: displayMoney(salary?.totalSalary),
    },
  ];

  return (
    <Box>
      <Typography
        variant="h5"
        sx={{
          textAlign: "center",
          fontWeight: "bold",
          mb: 3,
        }}
      >
        {t("salaryPaymentDetails")}
      </Typography>

      {fields.map((field, index) => (
        <React.Fragment key={index}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              gap: 2,
              py: 1,
            }}
          >
            <Typography
              sx={{
                fontWeight: 600,
                minWidth: "35%",
              }}
            >
              {field.label}:
            </Typography>

            <Typography
              sx={{
                textAlign: "right",
                whiteSpace: "pre-wrap",
                overflowWrap: "anywhere",
              }}
            >
              {field.value === null ||
              field.value === undefined ||
              field.value === ""
                ? "-"
                : field.value}
            </Typography>
          </Box>

          <Divider />
        </React.Fragment>
      ))}
    </Box>
  );
};

//------------------------------------------------
// Salary History
//------------------------------------------------
const SalaryHistory = (props) => {
  const { t } = useTranslation();

  const {
    _clear,
    _getById,
    _list,
    _type,
    _delete,
    _store,
  } = props;

  const { Periodic } = DropLi();
  const { setLoader } = useContext(AppContext);

  const reportRef = useRef(null);
  const detailsRef = useRef(null);

  const [showView, setShowView] = useState(false);
  const [viewData, setViewData] = useState(null);

  const [alert, setAlert] = useState({
    isOpen: false,
    type: "",
    title: "",
    subTitle: "",
  });

  //------------------------------------------------
  // Load Salary History
  //------------------------------------------------
  useEffect(() => {
    if (_store?.id) {
      setLoader(true);

      _getById(
        endpoints.Salary,
        _store.id,
        actions.ActionType.List,
        () => setLoader(false)
      );
    }

    return () => {
      _clear();
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [_store?.id]);

  //------------------------------------------------
  // Salary History Table Data
  //------------------------------------------------
  const tableData = useMemo(() => {
    if (
      _type !== "salaryHistories" ||
      !Array.isArray(_list)
    ) {
      return [];
    }

    return _list.map((obj) => {
      const user = obj.user || {};

      const salaryTypeName =
        Periodic.find(
          (item) =>
            String(item.id) === String(user.salaryType)
        )?.name || "";

      return {
        ...obj,
        firstName: user.firstName || "",
        lastName: user.lastName || "",
        dob: user.dob || "",
        salaryType: salaryTypeName,
        salary: user.salary,
      };
    });
  }, [_list, _type, Periodic]);

  //------------------------------------------------
  // View Salary Payment
  //------------------------------------------------
  const handleView = (data) => {
    const salary = tableData.find(
      (item) => String(item.id) === String(data.id)
    );

    setViewData(salary || data);
    setShowView(true);
  };

  const handleCloseView = () => {
    setShowView(false);
    setViewData(null);
  };

  //------------------------------------------------
  // Delete Salary Payment
  //------------------------------------------------
  const handleDlt = (id) => {
    setAlert({
      isOpen: true,
      type: "confirmation",
      title: t("areYouSure"),
      subTitle: t("deleteSalaryConfirmation"),

      onConfirm: () => {
        const onComplete = (res) => {
          if (res.status === 200) {
            setAlert({
              isOpen: true,
              type: "success",
              title: t("successful"),
              subTitle: res.msg,
            });
          } else {
            setAlert({
              isOpen: true,
              type: "error",
              title: t("error"),
              subTitle: res.msg,
            });
          }
        };

        _delete(
          endpoints.Salary,
          id,
          actions.ActionType.RmvList,
          onComplete
        );
      },
    });
  };

  //------------------------------------------------
  // Table Columns
  //------------------------------------------------
  const screenColumns = [
    { id: "actions", label: t("actions") },
    { id: "firstName", label: t("firstName") },
    { id: "lastName", label: t("lastName") },
    { id: "dob", label: t("dateOfBirth") },
    { id: "salaryType", label: t("salaryType") },
    { id: "salary", label: t("salary") },
    { id: "payDate", label: t("payPeriod") },
    { id: "totalHours", label: t("totalWorkHour") },
    { id: "totalSalary", label: t("totalSalary") },
  ];

  const printColumns = screenColumns.filter(
    (column) => column.id !== "actions"
  );

  //------------------------------------------------
  // Action Buttons
  //------------------------------------------------
  const ActionComp = (data) => (
    <Box sx={{ display: "flex", gap: 0.5 }}>
      {/* View */}
      <Tooltip arrow title={t("view")}>
        <IconButton
          onClick={() => handleView(data)}
          size="small"
          className="btn-primary"
        >
          <VisibilityRounded className="fs-18 m-2" />
        </IconButton>
      </Tooltip>

      {/* Delete */}
      <Tooltip arrow title={t("delete")}>
        <IconButton
          onClick={() => handleDlt(data.id)}
          size="small"
          className="btn-error"
        >
          <ClearRounded className="fs-18 m-2" />
        </IconButton>
      </Tooltip>
    </Box>
  );

  //------------------------------------------------
  // Render
  //------------------------------------------------
  return (
    <>
      {/* Header and Print Preview */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 2,
          mb: 2,
        }}
      >
        <Typography variant="h5">
          {t("salaryHistory")}
        </Typography>

        <ReportPrintButton
          reportRef={reportRef}
          paperSize="Letter"
          reportTitle={t("salaryHistory")}
        />
      </Box>

      {/* Normal Salary History Table */}
      <Table
        ActionComp={ActionComp}
        HeaderComp={() => <div />}
        tblHead={screenColumns}
        tblData={tableData}
      />

      {/* Printable Salary History */}
      <Box
        ref={reportRef}
        className="salary-history-print"
        sx={{
          position: "absolute",
          left: "-10000px",
          top: 0,
          width: "1000px",
          backgroundColor: "#fff",
          color: "#000",
          p: 2,
        }}
      >
        <Typography
          variant="h5"
          sx={{
            textAlign: "center",
            fontWeight: "bold",
            mb: 2,
          }}
        >
          {t("salaryHistory")}
        </Typography>

        <Table
          printMode={true}
          ActionComp={() => <div />}
          HeaderComp={() => <div />}
          tblHead={printColumns}
          tblData={tableData}
        />
      </Box>

      {/* Alert */}
      <Alert
        alert={alert}
        setAlert={setAlert}
      />

      {/* View Salary Payment Dialog */}
      {showView && viewData && (
        <Dialog
          show={showView}
          onClose={handleCloseView}
          maxWidth="sm"
        >
          <Box sx={{ p: 3 }}>
            {/* Print Preview */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                mb: 2,
              }}
            >
              <ReportPrintButton
                reportRef={detailsRef}
                paperSize="Letter"
                reportTitle={t("salaryPaymentDetails")}
              />
            </Box>

            {/* Screen Details */}
            <Box
              sx={{
                backgroundColor: "#fff",
                color: "#000",
                p: 2,
              }}
            >
              <SalaryDetailsContent
                salary={viewData}
                store={_store}
                t={t}
              />
            </Box>

            {/* Printable Details */}
            <Box
              ref={detailsRef}
              className="salary-history-details-print"
              sx={{
                position: "absolute",
                left: "-10000px",
                top: 0,
                width: "1000px",
                backgroundColor: "#fff",
                color: "#000",
                p: 2,
              }}
            >
              <SalaryDetailsContent
                salary={viewData}
                store={_store}
                t={t}
              />
            </Box>

            {/* Close */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                mt: 3,
              }}
            >
              <Button
                variant="contained"
                onClick={handleCloseView}
              >
                {t("close")}
              </Button>
            </Box>
          </Box>
        </Dialog>
      )}
    </>
  );
};

const mapStateToProps = (state) => ({
  _list: state.pos.list,
  _type: state.pos.type,
  _store: state.pos.defStore,
});

const mapActionToProps = {
  _getById: actions.getById,
  _delete: actions.Delete,
  _clear: actions.clear,
};

export default connect(
  mapStateToProps,
  mapActionToProps
)(SalaryHistory);
